import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { useCreateTemplate, useUpdateTemplate } from '@/hooks/useTemplates';
import { TemplateResponse } from '@/types/api';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: TemplateResponse | null;
}

export function TemplateModal({ isOpen, onClose, template }: TemplateModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    variables: [] as string[],
  });
  const [newVariable, setNewVariable] = useState('');

  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        content: template.content,
        variables: template.variables || [],
      });
    } else {
      setFormData({
        name: '',
        content: '',
        variables: [],
      });
    }
  }, [template, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.content.trim()) {
      alert('Nome e conteúdo são obrigatórios');
      return;
    }

    try {
      if (template) {
        await updateTemplate.mutateAsync({ id: template.id, template: formData });
      } else {
        await createTemplate.mutateAsync(formData);
      }
      onClose();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
    }
  };

  const addVariable = () => {
    if (newVariable.trim() && !formData.variables.includes(newVariable.trim())) {
      setFormData({
        ...formData,
        variables: [...formData.variables, newVariable.trim()]
      });
      setNewVariable('');
    }
  };

  const removeVariable = (variable: string) => {
    setFormData({
      ...formData,
      variables: formData.variables.filter(v => v !== variable)
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addVariable();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {template ? 'Editar Template' : 'Novo Template'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nome do Template</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Digite o nome do template"
              required
            />
          </div>

          <div>
            <Label htmlFor="content">Conteúdo da Mensagem</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Digite o conteúdo da mensagem..."
              rows={6}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use variáveis como {'{nome}'}, {'{telefone}'} para personalizar as mensagens
            </p>
          </div>

          <div>
            <Label>Variáveis Disponíveis</Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Digite uma variável (ex: nome, telefone)"
                className="flex-1"
              />
              <Button type="button" onClick={addVariable} variant="outline">
                Adicionar
              </Button>
            </div>
            
            {formData.variables.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.variables.map((variable) => (
                  <Badge key={variable} variant="secondary" className="flex items-center gap-1">
                    {variable}
                    <X 
                      className="w-3 h-3 cursor-pointer hover:text-red-500" 
                      onClick={() => removeVariable(variable)}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={createTemplate.isPending || updateTemplate.isPending}
            >
              {template ? 'Atualizar' : 'Criar'} Template
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}